import { Box, FormHelperText, InputLabel } from "@mui/material";
import { CountryCode, isValidPhoneNumber } from "libphonenumber-js";
import React, { useState } from "react";
import { Control, Controller, FieldError, RegisterOptions } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./PhoneInputField.css";

export interface PhoneInputFieldProps {
	name: string;
	control: Control<any>;
	label?: string;
	placeholder?: string;
	required?: boolean;
	disabled?: boolean;
	defaultCountry?: string;
	countryCodeEditable?: boolean;
	preferredCountries?: string[];
	rules?: RegisterOptions;
	error?: boolean | FieldError;
	helperText?: string;
	className?: string;
	onChange?: (value: string, countryData: any) => void;
}

export const validatePhoneNumber = (
	value: string | undefined,
	required: boolean = false,
	countryCode?: string
): boolean | string => {
	if (!value || value.trim() === "" || value === "+" || value === "880") {
		if (required) {
			return "Phone number is required";
		}
		return true;
	}

	const rawDigits = value.replace(/\D/g, "");
	if (rawDigits.length <= 3) {
		if (required) {
			return "Phone number is required";
		}
		return true;
	}

	const formattedValue = value.startsWith("+") ? value : `+${value}`;

	try {
		const targetCountry = countryCode
			? (countryCode.toUpperCase() as CountryCode)
			: undefined;

		const isValid = isValidPhoneNumber(formattedValue, targetCountry);
		if (!isValid) {
			return "Please enter a valid phone number";
		}
	} catch {
		if (rawDigits.length < 8 || rawDigits.length > 15) {
			return "Please enter a valid phone number";
		}
	}

	return true;
};

const PhoneInputField: React.FC<PhoneInputFieldProps> = ({
	name,
	control,
	label = "Phone Number",
	placeholder = "Enter phone number",
	required = false,
	disabled = false,
	defaultCountry = "bd",
	countryCodeEditable = false,
	preferredCountries = ["bd", "in", "us", "gb", "sa", "ae"],
	rules,
	error,
	helperText,
	className = "",
	onChange: customOnChange,
}) => {
	const [isFocused, setIsFocused] = useState<boolean>(false);
	const [currentCountry, setCurrentCountry] = useState<string>(defaultCountry);

	return (
		<Controller
			name={name}
			control={control}
			rules={{
				validate: (val) => validatePhoneNumber(val, required, currentCountry),
				...rules,
			}}
			render={({
				field: { onChange, value, ref },
				fieldState: { error: fieldError },
			}) => {
				const isError = Boolean(error || fieldError);
				const displayHelperText =
					helperText || (fieldError?.message as string) || "";

				const formattedValue = value
					? typeof value === "string" && value.startsWith("+")
						? value.slice(1)
						: String(value)
					: "";

				return (
					<Box
						className={`mui-phone-input-root ${
							isFocused ? "is-focused" : ""
						} ${isError ? "is-error" : ""} ${
							disabled ? "is-disabled" : ""
						} ${className}`}
					>
						{label && (
							<InputLabel
								shrink
								required={required}
								error={isError}
								className="MuiInputLabel-root"
							>
								{label}
							</InputLabel>
						)}

						<PhoneInput
							country={defaultCountry}
							preferredCountries={preferredCountries}
							value={formattedValue}
							disabled={disabled}
							countryCodeEditable={countryCodeEditable}
							placeholder={placeholder}
							enableSearch
							searchPlaceholder="Search country..."
							searchNotFound="No country found"
							inputProps={{
								name,
								required,
								ref,
							}}
							onFocus={() => setIsFocused(true)}
							onBlur={() => setIsFocused(false)}
							onChange={(phoneValue, countryData: any) => {
								if (countryData?.countryCode) {
									setCurrentCountry(countryData.countryCode);
								}
								const valueWithPlus = phoneValue
									? phoneValue.startsWith("+")
										? phoneValue
										: `+${phoneValue}`
									: "";
								onChange(valueWithPlus);
								customOnChange?.(valueWithPlus, countryData);
							}}
						/>

						{displayHelperText && (
							<FormHelperText
								className={`MuiFormHelperText-root ${isError ? "Mui-error" : ""}`}
								error={isError}
							>
								{displayHelperText}
							</FormHelperText>
						)}
					</Box>
				);
			}}
		/>
	);
};

export default PhoneInputField;
